'use client';

import { PortableText, type PortableTextComponents } from '@portabletext/react';
import type { PortableTextBlock } from '@portabletext/types';
import { motion } from 'motion/react';
import type { Variants } from 'motion/react';
import Link from 'next/link';
import { useMemo, type ReactNode } from 'react';

import {
  legalDocumentBodyItemVariants,
  legalDocumentMotionInstant,
} from '@/components/shared/motion';
import type { LegalHeadingAnchorMap } from '@/lib/legal-toc';
import { trackContactLinkClick } from '@/lib/analytics/contact-link-tracking';
import { useReducedMotion } from '@/lib/use-reduced-motion';

function wrapBlock(variants: Variants, node: ReactNode) {
  return (
    <motion.div variants={variants} className="min-w-0">
      {node}
    </motion.div>
  );
}

function buildComponents(
  itemVariants: Variants,
  headingIdByBlockKey: LegalHeadingAnchorMap,
): PortableTextComponents {
  const headingId = (blockKey: string | undefined) =>
    typeof blockKey === 'string' ? headingIdByBlockKey[blockKey] : undefined;

  return {
    block: {
      h1: ({ children, value }) =>
        wrapBlock(
          itemVariants,
          <h2
            id={headingId(value._key)}
            className="text-xl sm:text-2xl text-foreground font-bold tracking-tight scroll-mt-28"
          >
            {children}
          </h2>,
        ),
      h2: ({ children, value }) =>
        wrapBlock(
          itemVariants,
          <h2
            id={headingId(value._key)}
            className="text-xl sm:text-2xl text-foreground font-bold tracking-tight scroll-mt-28"
          >
            {children}
          </h2>,
        ),
      h3: ({ children, value }) =>
        wrapBlock(
          itemVariants,
          <h3
            id={headingId(value._key)}
            className="text-lg sm:text-xl text-foreground font-semibold tracking-tight scroll-mt-28"
          >
            {children}
          </h3>,
        ),
      h4: ({ children, value }) =>
        wrapBlock(
          itemVariants,
          <h4
            id={headingId(value._key)}
            className="text-base sm:text-lg text-foreground font-semibold tracking-tight scroll-mt-28"
          >
            {children}
          </h4>,
        ),
      normal: ({ children }) =>
        wrapBlock(
          itemVariants,
          <p className="text-sm sm:text-base text-muted-foreground font-light leading-relaxed">
            {children}
          </p>,
        ),
      blockquote: ({ children }) =>
        wrapBlock(
          itemVariants,
          <blockquote className="border-l-2 border-muted-foreground/30 pl-4 my-2 text-sm sm:text-base text-muted-foreground font-light italic leading-relaxed">
            {children}
          </blockquote>,
        ),
    },
    list: {
      bullet: ({ children }) =>
        wrapBlock(
          itemVariants,
          <ul className="list-disc pl-6 space-y-2 text-sm sm:text-base text-muted-foreground font-light leading-relaxed">
            {children}
          </ul>,
        ),
      number: ({ children }) =>
        wrapBlock(
          itemVariants,
          <ol className="list-decimal pl-6 space-y-2 text-sm sm:text-base text-muted-foreground font-light leading-relaxed">
            {children}
          </ol>,
        ),
    },
    listItem: {
      bullet: ({ children }) => <li>{children}</li>,
      number: ({ children }) => <li>{children}</li>,
    },
    marks: {
      strong: ({ children }) => <strong className="font-bold text-foreground">{children}</strong>,
      em: ({ children }) => <em className="italic">{children}</em>,
      link: ({ children, value }) => {
        const href = typeof value?.href === 'string' ? value.href : '';
        if (!href) return <span>{children}</span>;

        const isExternal =
          /^https?:\/\//i.test(href) || href.startsWith('mailto:') || href.startsWith('tel:');

        if (isExternal) {
          return (
            <Link
              href={href}
              className="border-b border-border text-foreground hover:text-[#BFB7A1] hover:border-[#BFB7A1] transition-all duration-300 ease-in-out"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackContactLinkClick(href, 'legal_content')}
            >
              {children}
            </Link>
          );
        }

        return (
          <Link
            href={href}
            className="border-b border-border text-foreground hover:text-[#BFB7A1] hover:border-[#BFB7A1] transition-all duration-300 ease-in-out"
            onClick={() => trackContactLinkClick(href, 'legal_content')}
          >
            {children}
          </Link>
        );
      },
    },
  };
}

export function LegalPortableText({
  value,
  headingIdByBlockKey = {},
}: {
  value: PortableTextBlock[];
  headingIdByBlockKey?: LegalHeadingAnchorMap;
}) {
  const reduce = useReducedMotion();
  const components = useMemo(
    () =>
      buildComponents(
        reduce ? legalDocumentMotionInstant : legalDocumentBodyItemVariants,
        headingIdByBlockKey,
      ),
    [reduce, headingIdByBlockKey],
  );

  return <PortableText value={value} components={components} />;
}
